import type { AnyAction, Store } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';
import { useEffect, useRef, useState } from 'react';

// biome-ignore lint/style/noRestrictedImports: persist dispatches the flux core actions
import { coreActions } from 'app-components/flux/core-actions';
import { getLogger, tracker } from 'app-components/utils/core';

import type { Config } from './config';
import { getCachedState } from './getCachedState';

const log = getLogger('useRehydratedStore.ts');

type ConfigureStore = (initialState: object) => Store;

type CreatePersistConfig = () => Promise<Config>;

async function getStore(
  configureStore: ConfigureStore,
  createPersistConfig: CreatePersistConfig,
  initialStateOverride?: object,
): Promise<Store<any, AnyAction, unknown>> {
  if (initialStateOverride) {
    log.log('Skipping rehydration and using initial state override instead.');
    return configureStore(initialStateOverride);
  }

  try {
    const persistConfig = await createPersistConfig();

    const initialState = await getCachedState(persistConfig);

    return configureStore(initialState);
  } catch (e) {
    log.error('Failed to get persisted state: ', e);
  }

  return configureStore({});
}

export function useRehydratedStore(
  configureStore: ConfigureStore,
  createPersistConfig: CreatePersistConfig,
  initialStateOverride?: object, // Pass this arg to skip rehydration and initialize the store with the provided state object.
) {
  const [store, setStore] = useState<Store | null>(null);

  const startedHydrate = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: hydrates the store once, on mount
  useEffect(() => {
    if (startedHydrate.current === true) {
      return;
    }

    startedHydrate.current = true;

    const start = DateTime.now();

    tracker.timeEvent(tracker.events.persistLoadComplete);
    tracker.track(tracker.events.persistLoadStart);

    getStore(configureStore, createPersistConfig, initialStateOverride).then((newStore) => {
      const duration = start.diffNow();
      log.log(`load from persist complete in ${Math.abs(duration.as('milliseconds'))}ms...`);

      tracker.track(tracker.events.persistLoadComplete, {
        Duration: Math.abs(duration.as('seconds')),
      });

      newStore.dispatch(coreActions.persistDataLoadComplete());

      setStore(newStore);
    });
  }, []);

  return store;
}
